import React, { useRef, useEffect, useState } from "react";
import { MarkerArea } from "markerjs2";
import { observer } from "mobx-react";
import { useStore } from "../../../../../contexts/store-provider";

interface ImageEditorProps {
  imageUrl: string;
  language: "en" | "ja" | "jp";
  onSave?: (dataUrl: string) => void;
  onClose?: () => void;
}

const localizations: Record<string, Record<string, string>> = {
  en: {
    defaultText: "Your Text Here",
    toolbarTextTool: "Text",
    toolbarArrowTool: "Arrow",
    toolbarEllipseTool: "Ellipse",
    toolbarRectangleTool: "Rectangle",
  },
  jp: {
    defaultText: "テキストを入力",
    toolbarTextTool: "テキスト",
    toolbarArrowTool: "矢印",
    toolbarEllipseTool: "楕円",
    toolbarRectangleTool: "四角形",
  },
};

const ImageEditor: React.FC<ImageEditorProps> = ({
  imageUrl,
  language,
  onSave,
  onClose,
}) => {
  const { smrStore } = useStore();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const markerAreaRef = useRef<MarkerArea | null>(null);

  useEffect(() => {
    if (!smrStore.medicalRecordImageDetail || !imageLoaded) return;

    const img = imgRef.current;
    if (!img) return;

    // Clean up previous marker area if exists
    if (markerAreaRef.current) {
      try {
        markerAreaRef.current.close();
      } catch {
        // ignore if already closed
      }
      markerAreaRef.current = null;
    }

    const markerArea = new MarkerArea(img);
    markerAreaRef.current = markerArea;

    markerArea.addEventListener("render", (event) => {
      const annotatedImg = event.dataUrl;
      if (onSave) onSave(annotatedImg);
    });

    markerArea.addEventListener("close", () => {
      if (onClose) onClose();
    });

    // Apply localization
    const localization = localizations[language] || localizations.en;
    markerArea.settings.defaultText = localization.defaultText;

    markerArea.settings.displayMode = "popup";

    // Show the marker area after a small delay to ensure image dimensions are calculated
    setTimeout(() => {
      markerArea.show();
    }, 100);

    return () => {
      if (markerAreaRef.current) {
        try {
          markerAreaRef.current.close();
        } catch {
          // ignore if already closed
        }
        markerAreaRef.current = null;
      }
    };
  }, [smrStore.medicalRecordImageDetail, language, imageLoaded, onSave, onClose]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div 
      style={{ position: "relative", width: "100%", height: "100%" }}
      >
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Editable"
        onLoad={handleImageLoad}
        style={{
          maxWidth: "100%",
          maxHeight: "100dvh",
          width: "auto",
          height: "auto",
          display: "block",
          margin: "0 auto",
          visibility: "hidden",
          position: "absolute",
        }}
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default observer(ImageEditor);